import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';

import { Store } from '../../../../../state/store.interfaces';
import { ParameterType } from '../../../../Reports/TransferGraph/interfaces';
import { transferStatuses, weightUnits } from '../../../../../config/commons';
import { organizeDataByVariable } from '../../../../Pdfs/TransferGraph/helpers';
import { isTransferTypeCapsule } from '../../../../../helpers/transfer-stocking';

import styles from './MiniCharts.module.scss';
import { MiniHistogramChart } from './MiniHistogramChart';
import { MiniTransferMetricChart } from './MiniTransferMetricChart';

interface Props {
  theme?: 'dark' | 'light';
}

export const MiniCharts = (props: Props) => {
  const { theme = 'dark' } = props;
  const [t] = useTranslation();
  
  const { capsules } = useSelector((state: Store) => state.weighingTransfers);
  const { transferParameters, transfer } = capsules;

  const containerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number>(140);

  useEffect(() => {
    if (!containerRef?.current) {
      return;
    }

    const updateChartWidth = () => {
      const charts = 8;

      if (containerRef.current) {
        const margin = 48;
        const padding = 10 * charts;
        const totalWidth = containerRef.current.clientWidth - padding - margin;
        const newWidth = totalWidth / charts;
        setChartWidth(Math.max(newWidth, 80));
      }
    };

    updateChartWidth();
    window.addEventListener('resize', updateChartWidth);
    return () => window.removeEventListener('resize', updateChartWidth);
  }, [containerRef?.current?.clientWidth]);

  const renderChart = () => {
    const result = organizeDataByVariable(transferParameters);
    const items: JSX.Element[] = [];

    if (transfer.status === transferStatuses.COMPLETED) {
      items.push(
        <MiniHistogramChart
          title={t('detail.pdf.histogramWeight')}
          theme={theme}
          width={chartWidth}
        />
      );
    }

    if (!isTransferTypeCapsule(transfer?.transports)) {
      return items;
    }

    for (const key in result) {
      const transferMetrics = result[key];

      if (key === 'animalsSmallerThan') {
        items.push(
          <MiniTransferMetricChart
            title={`${t(`transfers.parameters.${key}`, { value: transfer?.maxSmallAnimalsWeight })} ${weightUnits.G}`}
            transferMetrics={transferMetrics}
            theme={theme}
            parameter={key as ParameterType}
            width={chartWidth}
          />
        );
        continue;
      }

      items.push(
        <MiniTransferMetricChart
          title={`${t(`transfers.parameters.${key}`)}`}
          transferMetrics={transferMetrics}
          theme={theme}
          parameter={key as ParameterType}
          width={chartWidth}
        />
      );
    }

    return items;
  };
  
  return (
    <div
      className={styles.container}
      ref={containerRef}
    >
      {renderChart()}
    </div>
  );
};